import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { defaultVideoCallTaskInfo } from 'contexts/zoomCallContext/zoomCallContext.settings';
import { TaskDetailsProps } from 'models/tasks.types';
import type { RootState } from 'store';
import {
  callCRCDevice as callCallCRCDevice,
  cancelCallCRCDevice as callCancelCallCRCDevice,
  handleEndVideoCall as callHandleEndVideoCall
} from 'utils/zoomCallService';

import { VideoCallProps } from './videoCall.types';

const initialState: VideoCallProps = {
  appointmentId: '',
  interpreterAdded: false,
  isPiP: false,
  isSukiDismissed: false,
  isVideoCallOpen: false,
  pendingTask: null,
  taskInfo: defaultVideoCallTaskInfo
};

/**
 * Thunk to start a video call.
 *
 * Why:
 *   ZoomCallProvider is only mounted when isVideoCallOpen is true to avoid
 *   performance issues from heavy context re-renders. This thunk stores the
 *   task in Redux and sets isVideoCallOpen to true, which mounts the provider.
 *   The provider then reads pendingTask and auto-initiates the call.
 *
 * How:
 *   1. Store the task as pendingTask in Redux
 *   2. Set isVideoCallOpen to true (mounts ZoomCallProvider)
 *   3. ZoomCallProvider reads pendingTask on mount and starts the call
 *   4. Provider clears pendingTask after initiating
 */
export const startVideoCallThunk = createAsyncThunk(
  'videoCall/startVideoCall',
  async (task: TaskDetailsProps, { dispatch }) => {
    // Store the task first, then mount the provider
    dispatch(setPendingTask(task));
    dispatch(setSukiDismissed(false));
    dispatch(setIsVideoCallOpen(true));
    return task;
  }
);

/**
 * Thunk to call a CRC (Conference Room Connector) device during an active call.
 * Only works when ZoomCallProvider is mounted (call is active).
 */
export const callCRCDeviceThunk = createAsyncThunk(
  'videoCall/callCRCDevice',
  async (sipAddress: string, { rejectWithValue }) => {
    const success = await callCallCRCDevice(sipAddress);
    if (!success) {
      return rejectWithValue({ message: 'callCRCDevice is not registered - no active call' });
    }
    return sipAddress;
  }
);

/**
 * Thunk to cancel a CRC device call during an active call.
 * Only works when ZoomCallProvider is mounted (call is active).
 */
export const cancelCallCRCDeviceThunk = createAsyncThunk(
  'videoCall/cancelCallCRCDevice',
  async (_, { rejectWithValue }) => {
    const success = await callCancelCallCRCDevice();
    if (!success) {
      return rejectWithValue({ message: 'cancelCallCRCDevice is not registered - no active call' });
    }
    return true;
  }
);

/**
 * Thunk to end an active video call.
 * Safe to call even when no call is active - will silently succeed.
 *
 * Why safe to call anytime:
 *   Used in useLogout to ensure call is ended before logging out.
 *   If no call is active (provider not mounted), we just return success.
 */
export const handleEndVideoCallThunk = createAsyncThunk(
  'videoCall/handleEndVideoCall',
  async () => {
    // Safe to call even if no call is active - will return false but we don't reject
    await callHandleEndVideoCall();
    return true;
  }
);

const videoCallSlice = createSlice({
  initialState,
  name: 'videoCall',
  reducers: {
    resetVideoCall: () => initialState,
    setAppointmentId: (state, action: PayloadAction<string>) => {
      state.appointmentId = action.payload;
    },
    setInterpreterAdded: (state, action: PayloadAction<boolean>) => {
      state.interpreterAdded = action.payload;
    },
    setIsPiP: (state, action: PayloadAction<boolean>) => {
      state.isPiP = action.payload;
    },
    setIsVideoCallOpen: (state, action: PayloadAction<boolean>) => {
      state.isVideoCallOpen = action.payload;
    },
    setPendingTask: (state, action: PayloadAction<TaskDetailsProps | null>) => {
      state.pendingTask = action.payload;
    },
    setSukiDismissed: (state, action: PayloadAction<boolean>) => {
      state.isSukiDismissed = action.payload;
    },
    setTaskInfo: (state, action: PayloadAction<VideoCallProps['taskInfo']>) => {
      state.taskInfo = action.payload;
    }
  }
});

export const selectVideoCall = (state: RootState) => state.videoCall;
export const selectPendingTask = (state: RootState) => state.videoCall.pendingTask;

export const {
  resetVideoCall,
  setAppointmentId,
  setInterpreterAdded,
  setIsPiP,
  setIsVideoCallOpen,
  setPendingTask,
  setSukiDismissed,
  setTaskInfo
} = videoCallSlice.actions;

export default videoCallSlice.reducer;
